/**
 * copyToClipboard — robust copy utility for environments where the modern
 * Clipboard API is blocked (iframes, non-secure contexts, missing permission).
 *
 * Tries:
 *   1) navigator.clipboard.writeText (when secure-context allows it)
 *   2) hidden textarea + document.execCommand('copy') fallback
 *   3) window.prompt() so the user can copy manually as a last resort
 *
 * Returns true when the value reached the clipboard, false otherwise.
 * Never throws.
 */
export default async function copyToClipboard(text, { promptOnFail = false } = {}) {
  if (text === undefined || text === null) return false;
  const value = String(text);

  // 1) Modern API
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (_) { /* fall through */ }

  // 2) Legacy synchronous fallback
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) return true;
  } catch (_) { /* fall through */ }

  // 3) Last resort — let the user copy the value themselves
  if (promptOnFail) {
    try { window.prompt("Copy:", value); } catch (_) { /* */ }
  }
  return false;
}
